import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Star, Plus, Sparkles, History } from "lucide-react";
import { API } from "../App";
import ExerciseIntro from "../components/ExerciseIntro";
import { getExerciseInfo } from "../lib/exerciseData";
import { useI18n } from "../lib/i18n";
import ShareExerciseDialog from "../components/ShareExerciseDialog";
import { useSharePrompt } from "../hooks/useSharePrompt";

const GratitudePage = ({ user, logout }) => {
  const { locale, t } = useI18n();
  const { shareData, promptShare, closeSharePrompt } = useSharePrompt();
  const EXERCISE_INFO = getExerciseInfo(t);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [items, setItems] = useState(["", "", ""]);
  const [reflection, setReflection] = useState("");

  useEffect(() => { fetchEntries(); }, []);

  const fetchEntries = async () => {
    try { const response = await axios.get(`${API}/gratitude`, { withCredentials: true }); setEntries(response.data); }
    catch (error) { console.error("Error fetching entries:", error); }
    finally { setLoading(false); }
  };

  const handleSubmit = async () => {
    const validItems = items.filter(item => item.trim());
    if (validItems.length === 0) { toast.error(t("gratitude.addAtLeastOne")); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/gratitude`, { items: validItems, reflection: reflection || null }, { withCredentials: true });
      toast.success(t("gratitude.saved"));
      if (user?.role === "patient") promptShare(res.data, "gratitude");
      setItems(["", "", ""]); setReflection(""); fetchEntries();
    } catch (error) { toast.error(t("common.error")); }
    finally { setSubmitting(false); }
  };

  const updateItem = (index, value) => { const newItems = [...items]; newItems[index] = value; setItems(newItems); };
  const addItem = () => { if (items.length < 10) setItems([...items, ""]); };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat(locale === "fr" ? "fr-FR" : "en-US", { weekday: 'long', day: 'numeric', month: 'long' }).format(date);
  };

  return (
    <DashboardLayout user={user} logout={logout}>
      <div className="space-y-8" data-testid="gratitude-page">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold">{t("gratitude.title")}</h1>
          <p className="text-muted-foreground text-sm sm:text-base mt-1">{t("gratitude.subtitle")}</p>
        </div>
        <ExerciseIntro exerciseInfo={EXERCISE_INFO.gratitude} />
        <div className="grid lg:grid-cols-2 gap-8">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-xl flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-amber-500" />{t("gratitude.todayGrateful")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {items.map((item, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-amber-100 text-amber-600 dark:bg-amber-950 dark:text-amber-300 flex items-center justify-center flex-shrink-0 mt-1"><Star className="w-4 h-4" /></div>
                  <Textarea placeholder={`${t("gratitude.item")} ${index + 1}...`} value={item} onChange={(e) => updateItem(index, e.target.value)} rows={2} className="flex-1" data-testid={`gratitude-input-${index}`} />
                </div>
              ))}
              {items.length < 10 && (
                <Button variant="outline" onClick={addItem} className="w-full border-dashed" data-testid="add-gratitude-btn">
                  <Plus className="w-4 h-4 mr-2" />{t("gratitude.addGratitude")}
                </Button>
              )}
              <div className="space-y-2 pt-4">
                <label className="text-sm font-medium">{t("gratitude.reflectionLabel")}</label>
                <Textarea placeholder={t("gratitude.reflectionPlaceholder")} value={reflection} onChange={(e) => setReflection(e.target.value)} rows={3} data-testid="reflection-input" />
              </div>
              <Button onClick={handleSubmit} disabled={submitting} className="w-full bg-primary hover:bg-primary/90" data-testid="submit-gratitude-btn">
                {submitting ? t("exercise.saving") : t("gratitude.saveGratitudes")}
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-xl flex items-center gap-2">
                <History className="w-5 h-5" />{t("gratitude.pastGratitudes")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-4">{[1, 2, 3].map((i) => (<div key={i} className="animate-pulse h-32 bg-muted rounded-lg" />))}</div>
              ) : entries.length > 0 ? (
                <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2">
                  {entries.map((entry) => (
                    <div key={entry.entry_id} className="p-4 bg-amber-50/50 border border-amber-100 rounded-lg">
                      <p className="text-sm font-medium text-amber-700 mb-3">{formatDate(entry.created_at)}</p>
                      <ul className="space-y-2">
                        {entry.items.map((item, idx) => (<li key={idx} className="flex items-start gap-2 text-sm"><Star className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" /><span>{item}</span></li>))}
                      </ul>
                      {entry.reflection && (<p className="mt-3 pt-3 border-t border-amber-200 text-sm text-muted-foreground italic">"{entry.reflection}"</p>)}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Star className="w-12 h-12 text-amber-300 mx-auto mb-4" />
                  <p className="text-muted-foreground">{t("common.noEntriesYet")}</p>
                  <p className="text-sm text-muted-foreground text-sm sm:text-base mt-1">{t("gratitude.startNoting")}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
      <ShareExerciseDialog open={!!shareData} entryId={shareData?.entryId} exerciseType={shareData?.exerciseType} onClose={closeSharePrompt} />
    </DashboardLayout>
  );
};

export default GratitudePage;
